<template>
    <teleport to="body">
        <transition name="modal-fade">
            <div v-if="isOpen" class="gpet-modal" @click.self="handleBackdrop" :style="{ 'z-index': zIndex }">
                <div class="gpet-modal__dialog" :class="dialogClasses" role="dialog" aria-modal="true" :aria-labelledby="computedTitleId" :style="{ height: height }">
                    <div v-if="showCustomClose" class="gpet-modal__close custom-close" aria-label="Đóng" @click="close">×</div>
                    <div class="gpet-modal__header" v-if="$slots.header || title">
                        <slot name="header">
                            <h3 class="gpet-modal__title" :class="{ 'custom-title': isCustomTitle }" :id="computedTitleId">{{ title }}</h3>
                        </slot>
                        <button v-if="showClose" type="button" class="gpet-modal__close" aria-label="Đóng" @click="close">×</button>
                    </div>
                    <div class="gpet-modal__body">
                        <slot />
                    </div>
                    <div class="gpet-modal__footer" v-if="$slots.footer">
                        <slot name="footer" />
                    </div>
                </div>
            </div>
        </transition>
    </teleport>
</template>

<script>
export default {
    name: "ModalComp",
    props: {
        modelValue: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: ""
        },
        size: {
            type: String,
            default: "md", // sm | md | lg | xl
            validator: (val) => ["sm", "md", "lg", "xl", "auto"].includes(val)
        },
        closeOnBackdrop: {
            type: Boolean,
            default: true
        },
        closeOnEsc: {
            type: Boolean,
            default: true
        },
        showClose: {
            type: Boolean,
            default: true
        },
        zIndex: {
            type: Number,
            default: 100
        },
        height: {
            type: String,
            default: "600px"
        },
        isCustomTitle: {
            type: Boolean,
            default: true
        },
        showCustomClose: {
            type: Boolean,
            default: false
        },
        isBack: {
            type: Boolean,
            default: false
        }
    },
    emits: ["update:modelValue", "open", "close"],
    data() {
        return {
            titleId: `gpet-modal-title-${Math.random().toString(36).slice(2, 8)}`
        };
    },
    computed: {
        isOpen() {
            return this.modelValue;
        },
        dialogClasses() {
            return {
                "gpet-modal__dialog--sm": this.size === "sm",
                "gpet-modal__dialog--md": this.size === "md",
                "gpet-modal__dialog--lg": this.size === "lg",
                "gpet-modal__dialog--xl": this.size === "xl",
                "gpet-modal__dialog--auto": this.size === "auto"
            };
        },
        computedTitleId() {
            return this.title ? this.titleId : undefined;
        }
    },
    watch: {
        modelValue(newVal, oldVal) {
            if (newVal === oldVal) return;
            if (newVal) {
                this.onOpen();
            } else {
                this.onClose();
            }
        }
    },
    mounted() {
        if (this.modelValue) this.onOpen();
        window.addEventListener("keydown", this.onKeydown);
    },
    beforeUnmount() {
        window.removeEventListener("keydown", this.onKeydown);
        this.restoreScroll();
    },
    methods: {
        onKeydown(e) {
            if (!this.isOpen) return;
            if (this.closeOnEsc && e.key === "Escape") {
                this.close();
            }
        },
        handleBackdrop() {
            if (this.closeOnBackdrop) this.close();
        },
        open() {
            if (this.isOpen) return;
            this.$emit("update:modelValue", true);
        },
        close() {
            if (!this.isOpen) return;
            this.$emit("update:modelValue", false);
            if (this.isBack && this.$route.name !== "home") {
                this.goBackHome();
            }
        },
        onOpen() {
            this.$emit("open");
            this.lockScroll();
        },
        onClose() {
            this.$emit("close");
            this.restoreScroll();
        },
        lockScroll() {
            const body = document.body;
            if (body) {
                this._prevBodyOverflow = body.style.overflow;
                this._prevBodyPaddingRight = body.style.paddingRight;
                const viewportWidth = window.innerWidth;
                const clientWidth = document.documentElement.clientWidth;
                const scrollbarWidth = Math.max(0, viewportWidth - clientWidth);
                body.style.overflow = "hidden";
                if (scrollbarWidth > 0) {
                    body.style.paddingRight = `${scrollbarWidth}px`;
                }
                body.classList.add("gpet-modal-open");
            }
        },
        restoreScroll() {
            const body = document.body;
            if (!body) return;
            if (typeof this._prevBodyOverflow !== "undefined") {
                body.style.overflow = this._prevBodyOverflow;
                this._prevBodyOverflow = undefined;
            } else {
                body.style.overflow = "";
            }
            if (typeof this._prevBodyPaddingRight !== "undefined") {
                body.style.paddingRight = this._prevBodyPaddingRight;
                this._prevBodyPaddingRight = undefined;
            } else {
                body.style.paddingRight = "";
            }
            body.classList.remove("gpet-modal-open");
        },
        goBackHome() {
            const currentPath = window.location.pathname;    
            const clinic = currentPath.split("/").pop();
            window.location.href = `/${clinic}`;
        },
    }
};
</script>

<style scoped>
.gpet-modal {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
}

.gpet-modal__dialog {
    width: 100%;
    max-width: 600px;
    background-color: var(--white-color);
    border-radius: 10px;
    box-shadow: 0px 8px 40px 0px rgba(0, 0, 0, 0.12);
    /* overflow: hidden; */
    overflow-y: auto;
    height: 100%;
    position: relative;
}

.gpet-modal__dialog:has(.custom-close) {
  overflow: unset;
}

.gpet-modal__dialog--sm { max-width: 420px; }
.gpet-modal__dialog--md { max-width: 600px; }
.gpet-modal__dialog--lg { max-width: 800px; }
.gpet-modal__dialog--xl { max-width: 1000px; }
.gpet-modal__dialog--auto { max-width: 80%; }

.gpet-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 25px 20px 0;
    /* border-bottom: 1px solid var(--border-color); */
}

.gpet-modal__title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-color);
    position: relative;
    left: 50%;
    transform: translateX(-50%);
}

.custom-title {
    font-size: 25px;
    font-weight: 600;
    color: var(--primary-color);
    margin-bottom: 0;
}


.gpet-modal__close {
    appearance: none;
    -webkit-appearance: none;
    border: none;
    background: transparent;
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
    color: var(--text-color);
}

.custom-close {
    line-height: 1;
    cursor: pointer;
    color: var(--text-color);
    position: absolute;
    display: flex;
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.12);
    border-radius: 100px;
    right: 0;
    top: -55px;
    justify-content: center;
    align-items: center;
    font-size: 30px;
}

.gpet-modal__body {
    padding: 20px;
}

.gpet-modal__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 12px 20px 20px 20px;
}

.modal-fade-enter-active, .modal-fade-leave-active {
    transition: opacity 0.2s ease;
}
.modal-fade-enter-from, .modal-fade-leave-to {
    opacity: 0;
}
</style>

